// client/src/pages/secure/PropertySearchPage.js — Property Search for agents and staff.
//
//   Search        our listings + the MLS index (pending too); pick properties and share
//                 them with a client / CRM contact (a branded /shared/<token> page), or
//                 email / text them through the outbound module (lib/sendShim) — every
//                 send, sent or not, is logged on the client's timeline
//   Matches       new listings matching my clients' saved searches (queued; a task is
//                 made for the agent; NOTHING goes to the client until I send it)
//   Saved         saved searches I set up for my clients
//   Collections   what I shared, how often it was opened, what the client liked / asked
//   Market        area snapshot from the listing index for a listing presentation
//   Outside sites one-click searches on Zillow, LoopNet & co.
//   Zoning search every known property, every town, zoned for a chosen business (all
//                 staff incl. agents; not examiners) — components/secure/ZoningSearchTab
//   Feed          (managing broker / office manager) MLS feed status, sync, settings
//
// Agents see only their own book; the server enforces it (routes/propertySearch.js).
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';
import { Card, Empty, ErrorLine, Btn, Tabs, fmtDateTime, input } from '../../components/secure/territory/Shared';
import { addressLine, placeLine, factsLine, photoUrl, onImgError } from '../../utils/properties';
import OutsideSitesTab from '../../components/secure/OutsideSitesTab';
import ZoningSearchTab from '../../components/secure/ZoningSearchTab';
import { VirtualStagingBadge } from '../../components/shared/VirtualStagingBadge';
import DataList from '../../components/shared/DataList';

const API = '/api/property-search';
const qs = (o) => new URLSearchParams(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined)).toString();

// ---------------------------------------------------------------------------
// Recipient picker (clients and CRM contacts in my book)
// ---------------------------------------------------------------------------
const RecipientPicker = ({ value, onChange, clientsOnly = false }) => {
    const api = useApiClient();
    const [q, setQ] = useState('');
    const [found, setFound] = useState({ clients: [], contacts: [] });
    useEffect(() => {
        const t = setTimeout(() => { api.get(`${API}/recipients?${qs({ q })}`).then(setFound).catch(() => {}); }, 250);
        return () => clearTimeout(t);
    }, [api, q]);
    const rows = [...found.clients, ...(clientsOnly ? [] : found.contacts)];
    return (
        <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="rp-q">{clientsOnly ? 'Client' : 'Client or contact'}</label>
            {value
                ? <p className="text-sm mt-1"><strong>{value.name}</strong> {value.email ? `· ${value.email}` : ''} {value.phone ? `· ${value.phone}` : ''} <button type="button" className="text-indigo-700 underline ml-2" onClick={() => onChange(null)}>change</button></p>
                : (
                    <>
                        <input id="rp-q" className={input} placeholder="Type a name or email" value={q} onChange={(e) => setQ(e.target.value)} />
                        <ul className="max-h-40 overflow-y-auto border rounded mt-1 divide-y">
                            {rows.map((r) => (
                                <li key={`${r.kind}-${r.id}`}><button type="button" className="w-full text-left px-2 py-1 text-sm hover:bg-slate-50" onClick={() => onChange(r)}>
                                    {r.name} <span className="text-slate-500">· {r.detail}{r.email ? ` · ${r.email}` : ''}</span></button></li>
                            ))}
                            {!rows.length && <li className="px-2 py-1 text-sm text-slate-500">No one in your book matches.</li>}
                        </ul>
                    </>
                )}
        </div>
    );
};

const recipientBody = (r) => (r ? (r.kind === 'client' ? { client_id: r.id } : { crm_contact_id: r.id }) : {});

// ---------------------------------------------------------------------------
// Share / send panel
// ---------------------------------------------------------------------------
const SendPanel = ({ keys, alertIds = null, clientPreset = null, onDone, sendInstalled }) => {
    const api = useApiClient();
    const [to, setTo] = useState(clientPreset);
    const [f, setF] = useState({ title: '', message: '', subject: '', email: false, sms: false, expires_days: 60 });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const go = async () => {
        setBusy(true); setError(''); setResult(null);
        const channels = [f.email ? 'email' : null, f.sms ? 'sms' : null].filter(Boolean);
        const body = { ...recipientBody(to), title: f.title || undefined, message: f.message || undefined, subject: f.subject || undefined,
            expires_days: f.expires_days, ...(alertIds ? { alert_ids: alertIds } : { listing_keys: keys }) };
        try {
            if (channels.length) setResult(await api.post(`${API}/send`, { ...body, channels }));
            else setResult(await api.post(`${API}/shares`, body));
            if (onDone) onDone();
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };
    const link = result && (result.url || result.share_url);
    return (
        <div className="space-y-3">
            <RecipientPicker value={to} onChange={setTo} clientsOnly={!!alertIds} />
            <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-slate-700" htmlFor="sp-title">Collection title</label>
                    <input id="sp-title" className={input} value={f.title} onChange={set('title')} placeholder={`Properties for ${to ? to.name.split(' ')[0] : 'you'}`} /></div>
                <div><label className="block text-sm font-medium text-slate-700" htmlFor="sp-exp">Link expires after (days)</label>
                    <input id="sp-exp" type="number" min="1" max="365" className={input} value={f.expires_days} onChange={set('expires_days')} /></div>
            </div>
            <div><label className="block text-sm font-medium text-slate-700" htmlFor="sp-msg">Personal note (optional)</label>
                <textarea id="sp-msg" rows={3} className={input} value={f.message} onChange={set('message')} /></div>
            <fieldset className="text-sm">
                <legend className="font-medium text-slate-700">Send it now (optional)</legend>
                <label className="mr-4"><input type="checkbox" checked={f.email} onChange={set('email')} /> Email (branded, with photos)</label>
                <label><input type="checkbox" checked={f.sms} onChange={set('sms')} /> Text message (short, with the link)</label>
                {f.email && <input className={`${input} mt-2`} placeholder="Email subject (optional)" value={f.subject} onChange={set('subject')} aria-label="Email subject" />}
                {!sendInstalled && (f.email || f.sms) && (
                    <p className="mt-2 text-amber-800 bg-amber-50 rounded p-2">The sending module is not installed on this site yet, so nothing will be sent. The collection is still created and the attempt is logged on the client's timeline — copy the link and send it yourself.</p>
                )}
                <p className="mt-1 text-slate-500">Consent, do-not-contact and quiet hours are checked by the sending module, not here.</p>
            </fieldset>
            <ErrorLine error={error} />
            <Btn onClick={go} disabled={busy || !to}>{busy ? 'Working…' : (f.email || f.sms ? 'Create and send' : 'Create share link')}</Btn>
            {result && (
                <div className="rounded bg-emerald-50 p-3 text-sm text-emerald-900" role="status">
                    {link && <p>Link: <a className="underline break-all" href={link} target="_blank" rel="noreferrer">{link}</a> <button type="button" className="underline ml-2" onClick={() => navigator.clipboard && navigator.clipboard.writeText(link)}>copy</button></p>}
                    {(result.results || []).map((r) => (
                        <p key={r.channel}>{r.channel === 'email' ? 'Email' : 'Text'} to {r.to || '(none on file)'}: {r.sent ? 'sent' : r.queued ? 'queued for sending' : `not sent — ${r.reason}`}</p>
                    ))}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Search tab
// ---------------------------------------------------------------------------
const SearchTab = ({ sendInstalled }) => {
    const api = useApiClient();
    const [f, setF] = useState({ location: '', types: '', status: '', min_price: '', max_price: '', min_beds: '', min_sqft: '', zoning: '', min_cap_rate: '', sort: 'featured', include_pending: '1', page: 1 });
    const [data, setData] = useState(null);
    const [cfg, setCfg] = useState(null);
    const [error, setError] = useState('');
    const [picked, setPicked] = useState([]);
    const [panel, setPanel] = useState(null);
    const [saveTo, setSaveTo] = useState(null);
    const [saveMsg, setSaveMsg] = useState('');
    useEffect(() => { fetch('/api/public/properties/config').then((r) => r.json()).then(setCfg).catch(() => {}); }, []);
    const run = useCallback(async (filters) => {
        setError('');
        try { setData(await api.get(`${API}/search?${qs(filters)}`)); } catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { run({ include_pending: '1', sort: 'featured' }); }, [run]);
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? (e.target.checked ? '1' : '') : e.target.value }));
    const submit = (e) => { e.preventDefault(); const g = { ...f, page: 1 }; setF(g); run(g); };
    const page = (p) => { const g = { ...f, page: p }; setF(g); run(g); };
    const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k].slice(0, 25)));
    const saveSearch = async () => {
        setSaveMsg('');
        try {
            const r = await api.post(`${API}/saved-searches`, { client_id: saveTo.id, criteria: f });
            setSaveMsg(`Saved for ${saveTo.name}: “${r.title}”. New matches will make a task for their agent.`);
        } catch (e) { setSaveMsg(e.message); }
    };
    return (
        <div className="space-y-4">
            <Card>
                <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 items-end">
                    <label className="col-span-2 text-sm">Location<input className={input} value={f.location} onChange={set('location')} placeholder="City, ZIP, county or street" /></label>
                    <label className="text-sm">Type<select className={input} value={f.types} onChange={set('types')}><option value="">All</option>{(cfg ? cfg.types : []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></label>
                    <label className="text-sm">Status<select className={input} value={f.status} onChange={set('status')}><option value="">Sale or lease</option>{(cfg ? cfg.statuses : []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></label>
                    <label className="text-sm">Min $<input className={input} value={f.min_price} onChange={set('min_price')} inputMode="numeric" /></label>
                    <label className="text-sm">Max $<input className={input} value={f.max_price} onChange={set('max_price')} inputMode="numeric" /></label>
                    <label className="text-sm">Beds<input className={input} value={f.min_beds} onChange={set('min_beds')} inputMode="numeric" /></label>
                    <label className="text-sm">Min sq ft<input className={input} value={f.min_sqft} onChange={set('min_sqft')} inputMode="numeric" /></label>
                    <label className="text-sm">Zoning<input className={input} value={f.zoning} onChange={set('zoning')} /></label>
                    <label className="text-sm">Min cap %<input className={input} value={f.min_cap_rate} onChange={set('min_cap_rate')} inputMode="decimal" /></label>
                    <label className="text-sm">Sort<select className={input} value={f.sort} onChange={set('sort')}>{(cfg ? cfg.sorts : []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></label>
                    <label className="text-sm flex items-center gap-1"><input type="checkbox" checked={f.include_pending === '1'} onChange={set('include_pending')} /> Include under contract</label>
                    <Btn type="submit" onClick={submit}>Search</Btn>
                </form>
            </Card>
            <ErrorLine error={error} />
            {picked.length > 0 && (
                <Card title={`${picked.length} selected`} right={<div className="flex flex-wrap gap-2"><Btn onClick={() => setPanel(panel ? null : 'send')}>Share / send to client</Btn><Btn kind="secondary" onClick={() => setPicked([])}>Clear</Btn></div>}>
                    {panel === 'send' && <SendPanel keys={picked} sendInstalled={sendInstalled} onDone={() => {}} />}
                </Card>
            )}
            {data && (
                <Card title={`${data.total.toLocaleString()} found — ${data.description}`} right={data.sources && data.sources.mls.enabled
                    ? <span className="text-xs text-slate-500">MLS {data.sources.mls.shown ? `on · updated ${fmtDateTime(data.data_updated_at)}` : 'not current — own listings only'}</span>
                    : <span className="text-xs text-slate-500">Own listings only (MLS feed off)</span>}>
                    {data.listings.length === 0 && <Empty>No listings match.</Empty>}
                    <ul className="divide-y">
                        {data.listings.map((l) => (
                            <li key={l.key} className="py-2 flex gap-3 items-start">
                                <input type="checkbox" className="mt-2" checked={picked.includes(l.key)} onChange={() => toggle(l.key)} aria-label={`Select ${addressLine(l)}`} />
                                <span className="relative shrink-0">
                                    <img src={photoUrl(l)} onError={onImgError} alt="" className="w-24 h-16 object-cover rounded" loading="lazy" />
                                    {l.photos && l.photos[0] && l.photos[0].virtually_staged && <VirtualStagingBadge className="text-[9px] px-1" />}
                                </span>
                                <div className="flex-1 text-sm">
                                    <p><a href={l.path} target="_blank" rel="noreferrer" className="font-medium text-indigo-700 hover:underline">{addressLine(l)}</a>, {placeLine(l)}</p>
                                    <p className="text-slate-700">{l.price_label} · {l.type_label} · {l.status_label}{factsLine(l) ? ` · ${factsLine(l)}` : ''}</p>
                                    <p className="text-xs text-slate-500">{l.source === 'own' ? 'Our listing' : (l.attribution ? l.attribution.text : 'MLS')}{l.mls_number ? ` · MLS# ${l.mls_number}` : ''}
                                        {l.source === 'own' && l.property_id ? <> · <Link className="underline" to={`/flyer/${l.property_id}`}>flyer</Link></> : null}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                    {data.pages > 1 && (
                        <div className="flex gap-2 items-center justify-center mt-3 text-sm">
                            <Btn kind="secondary" disabled={data.page <= 1} onClick={() => page(data.page - 1)}>Previous</Btn>
                            <span>Page {data.page} of {data.pages}</span>
                            <Btn kind="secondary" disabled={data.page >= data.pages} onClick={() => page(data.page + 1)}>Next</Btn>
                        </div>
                    )}
                    {data.disclaimer && <p className="text-xs text-slate-500 mt-3">{data.disclaimer}</p>}
                </Card>
            )}
            <Card title="Save this search for a client">
                <p className="text-sm text-slate-600 mb-2">New listings that match make a task for the client's agent and show in the client's portal. Nothing is sent to the client automatically.</p>
                <RecipientPicker value={saveTo} onChange={setSaveTo} clientsOnly />
                <Btn className="mt-2" disabled={!saveTo} onClick={saveSearch}>Save search</Btn>
                {saveMsg && <p className="text-sm mt-2" role="status">{saveMsg}</p>}
            </Card>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Matches tab
// ---------------------------------------------------------------------------
const MatchesTab = ({ sendInstalled, canCheck }) => {
    const api = useApiClient();
    const [state, setState] = useState('queued');
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    const [sendFor, setSendFor] = useState(null);
    const [msg, setMsg] = useState('');
    const load = useCallback(() => { api.get(`${API}/matches?state=${state}`).then(setRows).catch((e) => setError(e.message)); }, [api, state]);
    useEffect(() => { load(); }, [load]);
    const mark = async (id, s) => { try { await api.patch(`${API}/matches/${id}`, { state: s }); load(); } catch (e) { setError(e.message); } };
    const check = async () => { try { const r = await api.post(`${API}/matches/check`, {}); setMsg(`Checked: ${r.queued || 0} new match(es) queued.`); load(); } catch (e) { setError(e.message); } };
    const byClient = rows.reduce((m, r) => { (m[r.client_id] ||= { name: r.client_name, email: r.email, phone: r.phone_number, rows: [] }).rows.push(r); return m; }, {});
    return (
        <Card title="New matches for my clients' saved searches" right={<div className="flex flex-wrap gap-2 items-center">
            <select className="p-1 border rounded text-sm" value={state} onChange={(e) => setState(e.target.value)} aria-label="Show">
                <option value="queued">Waiting</option><option value="reviewed">Reviewed</option><option value="sent">Sent</option><option value="dismissed">Dismissed</option>
            </select>{canCheck && <Btn kind="secondary" onClick={check}>Check now</Btn>}</div>}>
            <ErrorLine error={error} />
            {msg && <p className="text-sm" role="status">{msg}</p>}
            {!rows.length && <Empty>Nothing here.</Empty>}
            {Object.entries(byClient).map(([cid, g]) => (
                <div key={cid} className="border-t py-3">
                    <div className="flex flex-wrap justify-between gap-2">
                        <p className="font-medium"><Link className="text-indigo-700 hover:underline" to={`/dashboard/clients/${cid}`}>{g.name}</Link> <span className="text-sm text-slate-500">{g.rows.length} match(es)</span></p>
                        {state !== 'sent' && <Btn onClick={() => setSendFor(sendFor === cid ? null : cid)}>Send these to {g.name.split(' ')[0]}</Btn>}
                    </div>
                    <ul className="text-sm mt-1">
                        {g.rows.map((r) => (
                            <li key={r.alert_id} className="flex flex-wrap gap-2 items-center py-1">
                                <a className="text-indigo-700 hover:underline" href={`/properties/${r.listing_key}`} target="_blank" rel="noreferrer">{r.summary}</a>
                                <span className="text-slate-500">· {r.search_title} · {fmtDateTime(r.queued_at)}</span>
                                {state === 'queued' && <><button type="button" className="text-xs underline" onClick={() => mark(r.alert_id, 'reviewed')}>reviewed</button>
                                    <button type="button" className="text-xs underline text-red-700" onClick={() => mark(r.alert_id, 'dismissed')}>dismiss</button></>}
                            </li>
                        ))}
                    </ul>
                    {sendFor === cid && (
                        <div className="mt-2 border rounded p-3">
                            <SendPanel alertIds={g.rows.map((r) => r.alert_id)} clientPreset={{ kind: 'client', id: Number(cid), name: g.name, email: g.email, phone: g.phone }}
                                sendInstalled={sendInstalled} onDone={load} />
                        </div>
                    )}
                </div>
            ))}
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Saved searches tab
// ---------------------------------------------------------------------------
const SavedTab = () => {
    const api = useApiClient();
    const [rows, setRows] = useState([]);
    const [error, setError] = useState('');
    const load = useCallback(() => { api.get(`${API}/saved-searches`).then(setRows).catch((e) => setError(e.message)); }, [api]);
    useEffect(() => { load(); }, [load]);
    const del = async (id) => { if (!window.confirm('Delete this saved search?')) return; try { await api.delete(`${API}/saved-searches/${id}`); load(); } catch (e) { setError(e.message); } };
    return (
        <Card title="Saved searches for my clients">
            <ErrorLine error={error} />
            {!rows.length && <Empty>None yet. Save one from the Search tab, or your clients save their own on the website.</Empty>}
            <ul className="divide-y text-sm">
                {rows.map((s) => (
                    <li key={s.saved_search_id} className="py-2 flex flex-wrap gap-2 items-center">
                        <Link className="font-medium text-indigo-700 hover:underline" to={`/dashboard/clients/${s.client_id}`}>{s.client_name}</Link>
                        <span>{s.title}</span>
                        <span className="text-slate-500">· {s.created_by_agent_id ? 'set up by an agent' : 'saved by the client'} · {s.queued} waiting</span>
                        <a className="text-xs underline" href={`/properties?${qs(s.criteria || {})}`} target="_blank" rel="noreferrer">run</a>
                        <button type="button" className="text-xs underline text-red-700 ml-auto" onClick={() => del(s.saved_search_id)}>delete</button>
                    </li>
                ))}
            </ul>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Collections tab
// ---------------------------------------------------------------------------
const CollectionsTab = () => {
    const api = useApiClient();
    const [rows, setRows] = useState([]);
    const [events, setEvents] = useState({});
    const [error, setError] = useState('');
    const load = useCallback(() => { api.get(`${API}/shares`).then(setRows).catch((e) => setError(e.message)); }, [api]);
    useEffect(() => { load(); }, [load]);
    const revoke = async (id) => { if (!window.confirm('Withdraw this link? The client will no longer be able to open it.')) return; try { await api.post(`${API}/shares/${id}/revoke`, {}); load(); } catch (e) { setError(e.message); } };
    const showEvents = async (id) => {
        if (events[id]) { setEvents((x) => ({ ...x, [id]: null })); return; }
        try { const ev = await api.get(`${API}/shares/${id}/events`); setEvents((x) => ({ ...x, [id]: ev })); } catch (e) { setError(e.message); }
    };
    const origin = window.location.origin;
    return (
        <Card title="Shared property collections">
            <ErrorLine error={error} />
            {!rows.length && <Empty>Nothing shared yet.</Empty>}
            <ul className="divide-y text-sm">
                {rows.map((s) => {
                    const gone = s.revoked_at || new Date(s.expires_at) < new Date();
                    return (
                        <li key={s.share_id} className="py-2">
                            <div className="flex flex-wrap gap-2 items-center">
                                <strong>{s.title}</strong>
                                <span className="text-slate-500">→ {s.recipient_name || '—'} · {s.listing_count} properties · opened {s.view_count}× · {s.favorites} liked · {s.questions} questions</span>
                                {gone ? <span className="text-xs text-slate-500">{s.revoked_at ? 'withdrawn' : 'expired'}</span>
                                    : <><a className="text-xs underline" href={`${origin}${s.path}`} target="_blank" rel="noreferrer">open</a>
                                        <button type="button" className="text-xs underline" onClick={() => navigator.clipboard && navigator.clipboard.writeText(`${origin}${s.path}`)}>copy link</button>
                                        <button type="button" className="text-xs underline text-red-700" onClick={() => revoke(s.share_id)}>withdraw</button></>}
                                <button type="button" className="text-xs underline ml-auto" onClick={() => showEvents(s.share_id)}>activity</button>
                            </div>
                            {events[s.share_id] && (
                                <ul className="ml-4 mt-1 text-xs text-slate-600">
                                    {events[s.share_id].length === 0 && <li>No activity yet.</li>}
                                    {events[s.share_id].map((e, i) => <li key={i}>{fmtDateTime(e.occurred_at)} — {e.event_type}{e.listing_key ? ` ${e.listing_key}` : ''}{e.detail ? `: ${e.detail}` : ''}</li>)}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Market snapshot tab
// ---------------------------------------------------------------------------
const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString('en-US')}`);
const MarketTab = () => {
    const api = useApiClient();
    const [f, setF] = useState({ location: '', types: '' });
    const [cfg, setCfg] = useState(null);
    const [m, setM] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => { fetch('/api/public/properties/config').then((r) => r.json()).then(setCfg).catch(() => {}); }, []);
    const go = async (e) => { e.preventDefault(); setError(''); try { setM(await api.get(`${API}/market?${qs(f)}`)); } catch (x) { setError(x.message); } };
    const s = m && m.stats;
    return (
        <Card title="Area market snapshot">
            <form onSubmit={go} className="flex flex-wrap gap-2 items-end mb-3">
                <label className="text-sm">Area<input className={input} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="City, ZIP or county" /></label>
                <label className="text-sm">Type<select className={input} value={f.types} onChange={(e) => setF({ ...f, types: e.target.value })}><option value="">All</option>{(cfg ? cfg.types : []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></label>
                <Btn type="submit" onClick={go}>Show</Btn>
                {m && <Btn kind="secondary" onClick={() => window.print()}>Print</Btn>}
            </form>
            <ErrorLine error={error} />
            {s && (
                <div className="text-sm space-y-2">
                    <p className="font-medium">{m.area} · {m.types.join(', ')}</p>
                    <table className="w-full max-w-xl"><tbody>
                        <tr><td>For sale</td><td className="text-right">{s.for_sale.count}</td></tr>
                        <tr><td>Median list price</td><td className="text-right">{money(s.for_sale.median_price)}</td></tr>
                        <tr><td>Median list price per sq ft</td><td className="text-right">{money(s.for_sale.median_price_per_sqft)}</td></tr>
                        <tr><td>Median days on market (active)</td><td className="text-right">{s.for_sale.median_days_on_market ?? '—'}</td></tr>
                        <tr><td>For rent / lease</td><td className="text-right">{s.for_rent.count}</td></tr>
                        <tr><td>Median monthly rent</td><td className="text-right">{money(s.for_rent.median_rent)}</td></tr>
                        <tr><td>Under contract</td><td className="text-right">{s.pending.count}</td></tr>
                        {s.sold && <>
                            <tr><td>Sold (last {s.sold.window_days} days)</td><td className="text-right">{s.sold.count}</td></tr>
                            <tr><td>Median sold price</td><td className="text-right">{money(s.sold.median_price)}</td></tr>
                            <tr><td>Median days to close</td><td className="text-right">{s.sold.median_days_to_close ?? '—'}</td></tr>
                            <tr><td>Months of inventory</td><td className="text-right">{s.sold.months_of_inventory ?? '—'}</td></tr>
                        </>}
                    </tbody></table>
                    {s.sold_note && <p className="text-slate-600">{s.sold_note}</p>}
                    <p className="text-xs text-slate-500">{m.source_line}</p>
                    {m.disclaimer && <p className="text-xs text-slate-500">{m.disclaimer}</p>}
                </div>
            )}
        </Card>
    );
};

// ---------------------------------------------------------------------------
// Feed & settings tab (managing broker / office manager)
// ---------------------------------------------------------------------------
const FeedTab = ({ isBroker }) => {
    const api = useApiClient();
    const [st, setSt] = useState(null);
    const [set, setSet] = useState(null);
    const [vals, setVals] = useState({});
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');
    const load = useCallback(() => {
        api.get(`${API}/status`).then(setSt).catch((e) => setError(e.message));
        if (isBroker) api.get(`${API}/settings`).then((s) => { setSet(s); setVals(s.values); }).catch((e) => setError(e.message));
    }, [api, isBroker]);
    useEffect(() => { load(); }, [load]);
    const syncNow = async (mode) => { setMsg('Syncing…'); try { const r = await api.post(`${API}/sync`, { mode }); setMsg(r.skipped ? `Skipped: ${r.reason}` : `${r.outcome}: ${r.upserted} updated, ${r.removed} removed${r.error ? ` — ${r.error}` : ''}`); load(); } catch (e) { setMsg(''); setError(e.message); } };
    const reindex = async () => { try { const r = await api.post(`${API}/reindex-own`, {}); setMsg(r.skipped ? r.skipped : `Own listings re-indexed: ${r.indexed}.`); load(); } catch (e) { setError(e.message); } };
    const save = async () => { try { const r = await api.put(`${API}/settings`, vals); setVals(r.values); setMsg('Settings saved.'); } catch (e) { setError(e.message); } };
    const typesOn = String(vals.properties_types || '').split(',').filter(Boolean);
    const toggleType = (k) => {
        const cur = typesOn.length ? typesOn : (set ? set.types.map((t) => t.key) : []);
        const next = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
        setVals({ ...vals, properties_types: next.join(',') });
    };
    const p = st && st.provider;
    return (
        <div className="space-y-4">
            <ErrorLine error={error} />
            {msg && <p className="text-sm" role="status">{msg}</p>}
            {st && (
                <Card title="MLS listing feed (IDX)" right={isBroker && <div className="flex flex-wrap gap-2"><Btn onClick={() => syncNow('incremental')} disabled={!p.enabled}>Sync now</Btn><Btn kind="secondary" onClick={() => syncNow('full')} disabled={!p.enabled}>Full reload</Btn><Btn kind="secondary" onClick={reindex}>Re-index our listings</Btn></div>}>
                    {st.migration_needed && <p className="text-sm text-amber-800 bg-amber-50 rounded p-2 mb-2">The property search migration (2026_10_03_property_search.sql) has not been run on this site: the public page reads our own listings directly and the MLS feed cannot run.</p>}
                    <div className="overflow-x-auto"><table className="text-sm"><tbody>
                        <tr><td className="pr-4 text-slate-500">Status</td><td>{p.enabled ? 'ON' : p.switched_on ? `switched on but not configured — missing ${p.missing.join(', ')}` : 'OFF (IDX_ENABLED is not true in .env)'}</td></tr>
                        <tr><td className="pr-4 text-slate-500">Provider</td><td>{p.provider_label || '—'}{p.host ? ` · ${p.host}` : ''}{p.originating_systems && p.originating_systems.length ? ` · ${p.originating_systems.join(', ')}` : ''}</td></tr>
                        <tr><td className="pr-4 text-slate-500">MLS name</td><td>{p.mls_name || '—'}</td></tr>
                        <tr><td className="pr-4 text-slate-500">Limits</td><td>{p.rate ? `${p.rate.per_second}/s · ${p.rate.per_hour}/h${p.rate.per_day ? ` · ${p.rate.per_day}/day` : ''} · page ${p.rate.page_size}` : '—'}</td></tr>
                        <tr><td className="pr-4 text-slate-500">Index</td><td>{st.index ? `${st.index.own} own · ${st.index.idx} MLS · ${st.index.duplicates} duplicates of ours hidden` : '—'}</td></tr>
                        {st.state && <>
                            <tr><td className="pr-4 text-slate-500">Last success</td><td>{fmtDateTime(st.state.last_success_at)} {st.fresh === false && <span className="text-red-700">(stale — MLS listings hidden from the public site)</span>}</td></tr>
                            <tr><td className="pr-4 text-slate-500">Last outcome</td><td>{st.state.last_outcome || '—'}{st.state.last_error ? ` — ${st.state.last_error}` : ''}{st.state.running ? ' (running now)' : ''}</td></tr>
                            <tr><td className="pr-4 text-slate-500">High-water mark</td><td>{st.state.high_water_mark || '—'}</td></tr>
                        </>}
                        <tr><td className="pr-4 text-slate-500">Email / text sending</td><td>{st.send_installed ? 'installed' : 'not installed (shares are created and logged; nothing is sent)'}</td></tr>
                    </tbody></table></div>
                    {st.runs && st.runs.length > 0 && (
                        <div className="mt-3 text-xs"><DataList rows={st.runs} rowKey="run_id" csvName="idx-sync-runs" defaultSort={{ key: 'started_at', dir: 'desc' }}
                            filters={[{ key: 'run_mode', label: 'Mode' }, { key: 'outcome', label: 'Outcome' }]}
                            columns={[
                                { key: 'started_at', label: 'Started', type: 'date', render: (r) => <span title={r.error_text || ''}>{fmtDateTime(r.started_at)}</span> },
                                { key: 'run_mode', label: 'Mode' },
                                { key: 'trigger_source', label: 'By' },
                                { key: 'outcome', label: 'Outcome', value: (r) => `${r.outcome || '…'}${r.error_text ? ` — ${r.error_text}` : ''}`, sortValue: (r) => r.outcome || '', render: (r) => <span title={r.error_text || ''}>{r.outcome || '…'}</span> },
                                { key: 'fetched', label: 'Fetched', type: 'number' },
                                { key: 'upserted', label: 'Updated', type: 'number' },
                                { key: 'removed', label: 'Removed', type: 'number' },
                                { key: 'requests', label: 'Requests', type: 'number' },
                                { key: 'retries', label: 'Retries', type: 'number' },
                            ]} /></div>
                    )}
                    <p className="text-xs text-slate-500 mt-2">Credentials live in the server's .env (never on this screen). See the Operations Manual, “Property search and IDX”.</p>
                </Card>
            )}
            {isBroker && set && (
                <Card title="Property search settings" right={<Btn onClick={save}>Save</Btn>}>
                    <div className="space-y-3 text-sm">
                        <fieldset><legend className="font-medium">Property types shown on the website</legend>
                            {set.types.map((t) => <label key={t.key} className="mr-4 inline-block"><input type="checkbox" checked={typesOn.length ? typesOn.includes(t.key) : true} onChange={() => toggleType(t.key)} /> {t.label}</label>)}
                        </fieldset>
                        <label className="block"><input type="checkbox" checked={vals.properties_show_sold === '1'} onChange={(e) => setVals({ ...vals, properties_show_sold: e.target.checked ? '1' : '0' })} /> Show sold / leased listings (only if the MLS agreement permits it)</label>
                        <label className="block"><input type="checkbox" checked={vals.properties_sitemap_mls === '1'} onChange={(e) => setVals({ ...vals, properties_sitemap_mls: e.target.checked ? '1' : '0' })} /> List MLS listings in sitemap.xml (ours are always listed)</label>
                        <label className="block">MLS name (disclaimer and attribution)<input className={input} value={vals.properties_mls_name || ''} onChange={(e) => setVals({ ...vals, properties_mls_name: e.target.value })} placeholder={set.mls_example} /></label>
                        <label className="block">MLS sources NOT to display (OriginatingSystemName, comma separated)<input className={input} value={vals.properties_hidden_sources || ''} onChange={(e) => setVals({ ...vals, properties_hidden_sources: e.target.value })} /></label>
                        <div className="grid sm:grid-cols-2 gap-2">
                            <label className="block">Office phone on listings<input className={input} value={vals.properties_contact_phone || ''} onChange={(e) => setVals({ ...vals, properties_contact_phone: e.target.value })} /></label>
                            <label className="block">Office email on listings<input className={input} value={vals.properties_contact_email || ''} onChange={(e) => setVals({ ...vals, properties_contact_email: e.target.value })} /></label>
                        </div>
                        <label className="block">IDX disclaimer — paste the MLS's required wording from the signed IDX agreement. {'{mls_name}'} and {'{updated_at}'} are filled in.
                            <textarea rows={5} className={input} value={vals.properties_disclaimer || ''} onChange={(e) => setVals({ ...vals, properties_disclaimer: e.target.value })} placeholder={set.default_disclaimer} /></label>
                        <p className="text-xs text-slate-500">Blank = the {set.state} default shown in grey. Brokerage name, city/state and the virtual-office link come from Virtual Office Settings.</p>
                    </div>
                </Card>
            )}
        </div>
    );
};

const PropertySearchPage = () => {
    const user = getCurrentUser();
    const isBroker = !!(user && user.isManagingBroker);
    const isAdmin = canManageStaff(user);
    const api = useApiClient();
    const canZone = !!user && user.role !== 'examiner';
    const [params, setParams] = useSearchParams();
    const [sendInstalled, setSendInstalled] = useState(false);
    useEffect(() => { api.get(`${API}/send/status`).then((r) => setSendInstalled(!!r.installed)).catch(() => {}); }, [api]);
    const tabs = [['search', 'Search'], ['matches', 'Matches'], ['saved', 'Saved searches'], ['collections', 'Collections'], ['market', 'Market snapshot'], ['outside', 'Outside sites'],
        ...(canZone ? [['zoning', 'Zoning search']] : []),
        ...(isAdmin ? [['feed', 'Feed & settings']] : [])];
    // The tab lives in the address (?tab=zoning, ?tab=saved …) so the menu's Zoning Search /
    // Saved Searches links and the agent home tiles open the right one, even from this page.
    const urlTab = params.get('tab');
    const tab = tabs.some(([k]) => k === urlTab) ? urlTab : 'search';
    const setTab = (t) => setParams(t === 'search' ? {} : { tab: t }, { replace: true });
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-800">Property Search</h1>
                <a href="/properties" target="_blank" rel="noreferrer" className="text-sm text-indigo-700 underline">Public Properties page</a>
            </div>
            <Tabs tabs={tabs} value={tab} onChange={setTab} />
            {tab === 'search' && <SearchTab sendInstalled={sendInstalled} />}
            {tab === 'matches' && <MatchesTab sendInstalled={sendInstalled} canCheck={isAdmin} />}
            {tab === 'saved' && <SavedTab />}
            {tab === 'collections' && <CollectionsTab />}
            {tab === 'market' && <MarketTab />}
            {tab === 'outside' && <OutsideSitesTab isBroker={isBroker} />}
            {tab === 'zoning' && canZone && <ZoningSearchTab />}
            {tab === 'feed' && isAdmin && <FeedTab isBroker={isBroker} />}
        </div>
    );
};

export default PropertySearchPage;
